import { useState, useEffect, useRef } from "react";

/**
 * Custom hook to track column widths
 * @param {Object} tableRef - Reference to the table element
 * @param {boolean} showCheckbox - Whether checkbox column is shown
 * @param {boolean} showSlNo - Whether Sl No column is shown
 * @returns {Object} - Object containing columnWidths array and related measurements
 */
export const useColumnWidths = (
  tableRef,
  showCheckbox = true,
  showSlNo = true
) => {
  const [columnWidths, setColumnWidths] = useState([]);
  const [checkboxWidth, setCheckboxWidth] = useState(48);
  const [slNoWidth, setSlNoWidth] = useState(64);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    // Initialize column widths
    const updateColumnWidths = () => {
      if (!tableRef.current) return;

      const headerCells = tableRef.current.querySelectorAll(
        "thead tr:first-child th"
      );
      if (!headerCells.length) return;

      const cells = Array.from(headerCells);
      let startIndex = 0;

      // Measure checkbox width if shown
      if (showCheckbox && cells.length > 0) {
        setCheckboxWidth(cells[0].offsetWidth);
        startIndex++;
      }

      // Measure Sl No width if shown
      if (showSlNo && cells.length > startIndex) {
        setSlNoWidth(cells[startIndex].offsetWidth);
        startIndex++;
      }

      // Measure data column widths
      const dataColumnWidths = cells
        .slice(startIndex)
        .map((cell) => cell.offsetWidth || 100); // Default to 100px if width not available

      setColumnWidths(dataColumnWidths);
    };

    // Set up resize observer to track column width changes
    if (tableRef.current) {
      updateColumnWidths();

      // Create resize observer
      resizeObserverRef.current = new ResizeObserver(() => {
        updateColumnWidths();
      });

      // Observe the table element
      resizeObserverRef.current.observe(tableRef.current);

      // Also update on window resize
      window.addEventListener("resize", updateColumnWidths);
    }

    return () => {
      // Clean up
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener("resize", updateColumnWidths);
    };
  }, [tableRef, showCheckbox, showSlNo]);

  return { columnWidths, checkboxWidth, slNoWidth };
};

/**
 * Custom hook to provide CSS classes and styles for fixed columns
 * @param {number} fixedCol - Number of fixed columns
 * @param {string} color - Color theme for the table (default: 'primary')
 * @returns {Object} - Object containing CSS class names and styles
 */
export const useFixedColumns = (fixedCol = 0, color = "primary") => {
  // Generate styles for fixed columns with smooth transitions and box shadow
  const fixedColumnStyles = {
    position: "sticky",
    zIndex: 10,
    transition: "box-shadow 0.2s ease-in-out, left 0.2s ease-in-out",
    boxShadow: fixedCol > 0 ? "4px 0 8px -4px rgba(0, 0, 0, 0.15)" : "none",
  };

  return {
    stickyCell: "sticky z-10",
    fixedCellBg: `bg-white `,
    headerFixedCellBg: `bg-${color}-50 `,
    fixedColumnStyles,
  };
};
